import { liveReload } from '@shared/services/realtime.service';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { ListView } from '@app/shared/utils/list-view';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';

import { LogisticsCatalogs, MaterialRequestsService, fmtDate, statusChip, errMsg, canSupplyRequests, currentUserId } from '../logistics.service';
import { StatusNoteDialogComponent } from '../status-note-dialog.component';
import { RequestFormDialogComponent } from './request-form-dialog.component';
import { SupplyDialogComponent } from './supply-dialog.component';

export const REQUEST_STATUSES = ['Nueva', 'En surtido', 'Surtida', 'Recibida', 'No aprobada', 'Cancelada'];

/**
 * Solicitud de material (SOL-n). Proceso:
 *   1  El usuario del hospital registra la solicitud (queda como quien
 *      solicita) y se avisa por correo a mesa de control. Ya no se edita.
 *   2  Mesa de control I/II o un administrador la surte: ve dónde hay cada
 *      código en todo el proyecto (hospitales/lockers y FSL por SAP) con el
 *      consumo de cada hospital y registra de dónde sale. El Forms y el
 *      correo del surtido están pendientes; ya no se hace el traspaso aquí.
 */
@Component({
  selector: 'app-material-requests',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, MatTableModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule, MatButtonToggleModule, MatDatepickerModule, MatDialogModule,
    KpiStripComponent, PageLoaderComponent, PageTitleComponent, SearchPillComponent, FilterChipsComponent],
  templateUrl: './material-requests.component.html',
  styleUrl: '../logistics.scss',
})
export class MaterialRequestsComponent implements OnInit {
  private readonly modules = inject(ModulesService);
  readonly title = this.modules.moduleTitle('/materialRequests', 'Solicitud de material');

  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(MaterialRequestsService);
  private readonly dialog = inject(MatDialog);
  private readonly msg = inject(CustomMessageService);
  private readonly excel = inject(SimpleExcelService);
  private readonly scopeService = inject(UserScopeService);
  private scope: UserScope = { places: null, specialties: null };
  /** Surtir: mesa de control I y II y administradores (el API lo vuelve a validar). */
  canSupply = false;

  readonly statuses = REQUEST_STATUSES;
  readonly fmtDate = fmtDate;
  readonly statusChip = statusChip;
  readonly listView = new ListView('materialRequests', 'table');
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['materialRequests'], () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));

  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  statusFilter = '';
  placeFilter = '';
  searchText = '';

  columns = ['folio', 'createdOn', 'requestedBy', 'place', 'lines', 'progress', 'neededBy', 'status', 'actions'];

  /** ¿Puede cancelar? Quien la registró, mesa de control o un administrador (el API lo valida). */
  canCancel(r: any): boolean {
    return ['Nueva', 'En surtido', 'No aprobada'].includes(r.status) && (this.canSupply || r.createdBy === currentUserId());
  }

  /** Cambia el estado desde el menú de 3 puntos. */
  async changeStatus(r: any, status: 'No aprobada' | 'En surtido' | 'Cancelada'): Promise<void> {
    const cfg = {
      'No aprobada': { title: 'No aprobar solicitud', label: 'Motivo', required: true, accept: 'No aprobar' },
      'En surtido': { title: 'Pasar a En surtido', label: 'Comentario (opcional)', required: false, accept: 'Pasar a En surtido' },
      'Cancelada': { title: 'Cancelar solicitud', label: 'Motivo (opcional)', required: false, accept: 'Sí, cancelar' },
    }[status];
    const res = await new Promise<any>(ok => this.dialog.open(StatusNoteDialogComponent, {
      data: { ...cfg, message: `Solicitud <b>${r.folio}</b> · ${r._place}` }, width: '440px', maxWidth: '95vw',
    }).afterClosed().subscribe(ok));
    if (!res) return;
    try {
      await this.service.setStatus(r.id, status, res.note || null);
      await this.msg.open({ title: 'Listo', type: 'success', message: `Solicitud <b>${r.folio}</b>: ${status}.`, duration: 2000 });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo cambiar el estado.'), dismissBtn: true });
    }
  }
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI: Record<string, (r: any) => boolean> = {
    new: r => r.status === 'Nueva',
    progress: r => r.status === 'En surtido',
    done: r => r.status === 'Surtida' || r.status === 'Recibida',
    rejected: r => r.status === 'No aprobada',
    late: r => r._late,
  };

  get placeOptions(): any[] { return this.cat.places.filter(p => inScope(this.scope.places, p.id, false) && this.cat.sourceKind({ place: p.id }) === 'HOSPITAL'); }

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.initialLoad()); }

  private async initialLoad(): Promise<void> {
    try { this.scope = await this.scopeService.load(); } catch { /* sin alcance */ }
    this.canSupply = canSupplyRequests(this.scope.roles);
    await this.cat.load();
    await this.load();
  }

  async load(): Promise<void> {
    try {
      const condition: any = { deleted: false };
      if (this.placeFilter) condition.place = this.placeFilter;
      const r: any = await this.service.search({ searchType: 'default', condition });
      const today = new Date().toISOString().slice(0, 10);
      this.allRows = ((r?.data ?? []) as any[])
        .filter(x => inScope(this.scope.places, x.place))
        .filter(x => !this.statusFilter || x.status === this.statusFilter)
        .filter(x => inDateRange(x.createdOn, this.periodStart, this.periodEnd))
        .map(x => {
          const lines: any[] = x.lines ?? [];
          const qty = lines.reduce((a, l) => a + (Number(l.quantity) || 0), 0);
          const sup = lines.reduce((a, l) => a + Math.min(Number(l.supplied) || 0, Number(l.quantity) || 0), 0);
          const plan: any[] = x.supplyPlan ?? [];
          const planned = plan.reduce((a, p) => a + (Number(p.quantity) || 0), 0);
          return {
            ...x, _place: this.cat.placeName(x.place), _warehouse: this.cat.warehousesById[x.warehouse]?.name ?? '',
            _qty: qty, _supplied: sup, _planned: planned, _pct: qty ? Math.round(100 * Math.min(planned, qty) / qty) : 0,
            _plan: plan.map(p => `${p.quantity} de ${p.label || (p.sourceType === 'FSL' ? 'FSL' : '')}${(r_codes(lines) > 1) ? ' (' + p.code + ')' : ''}`).join(' · '),
            _codes: lines.map(l => `${l.code} ×${l.quantity}`).join(', '),
            _late: !!x.neededBy && String(x.neededBy).slice(0, 10) < today && ['Nueva', 'En surtido'].includes(x.status),
          };
        })
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.dataSource.filterPredicate = (row, f) => [row.folio, row._place, row._warehouse, row.requestedBy, row._codes, row._plan, row.status].join(' ').toLowerCase().includes(f);
      this.refreshKpis();
      this.dataSource.paginator = this.paginator;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar las solicitudes.'), dismissBtn: true });
    }
  }

  private refreshKpis(): void {
    const c = (k: string) => this.allRows.filter(MaterialRequestsComponent.KPI[k]).length;
    this.kpis = [
      { key: 'total', label: 'Solicitudes', icon: 'assignment', value: this.allRows.length, isTotal: true },
      { key: 'new', label: 'Nuevas', icon: 'fiber_new', value: c('new'), tone: 'warn' },
      { key: 'progress', label: 'En surtido', icon: 'local_shipping', value: c('progress') },
      { key: 'done', label: 'Surtidas / recibidas', icon: 'task_alt', value: c('done'), tone: 'ok' },
      { key: 'rejected', label: 'No aprobadas', icon: 'block', value: c('rejected') },
      { key: 'late', label: 'Vencidas', icon: 'alarm', value: c('late'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(k: string | null): void { this.activeKpi = k; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? MaterialRequestsComponent.KPI[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  applySearch(v: string): void { this.searchText = v ?? ''; this.dataSource.filter = this.searchText.trim().toLowerCase(); }

  get filterChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.placeFilter) c.push({ key: 'place', label: 'Hospital', value: this.cat.placeName(this.placeFilter) });
    if (this.statusFilter) c.push({ key: 'status', label: 'Estado', value: this.statusFilter });
    if (this.periodStart || this.periodEnd) c.push({ key: 'period', label: 'Periodo', value: `${fmtDate(this.periodStart, false) || '…'} – ${fmtDate(this.periodEnd, false) || '…'}`, removable: false });
    return c;
  }
  async removeChip(k: string): Promise<void> {
    if (k === 'place') this.placeFilter = '';
    if (k === 'status') this.statusFilter = '';
    await this.load();
  }
  async clearFilters(): Promise<void> {
    this.placeFilter = ''; this.statusFilter = '';
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    this.applySearch('');
    await this.load();
  }

  openForm(): void {
    this.dialog.open(RequestFormDialogComponent, {
      data: { scopePlaces: this.scope.places }, width: '760px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: 'first-tabbable',
    }).afterClosed().subscribe(ok => { if (ok) void this.load(); });
  }

  /** Dónde hay el material y de dónde se surte (mesa de control I/II y administradores). */
  supply(r: any): void {
    if (!this.canSupply) return;
    this.dialog.open(SupplyDialogComponent, { data: { request: r }, maxWidth: '96vw', maxHeight: '92vh' })
      .afterClosed().subscribe(ok => { if (ok) void this.load(); });
  }

  async cancel(r: any): Promise<void> {
    const yes = await this.msg.open({ title: 'Cancelar solicitud', type: 'warning', acceptBtn: true, dismissBtn: true,
      acceptBtnText: 'Sí, cancelar', dismissBtnText: 'No', message: `¿Cancelar la solicitud <b>${r.folio}</b>?` });
    if (!yes) return;
    try {
      const { _place, _warehouse, _qty, _supplied, _planned, _pct, _codes, _plan, _late, ...rec } = r;
      await this.service.update({ ...rec, status: 'Cancelada', operationType: 'single' });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo cancelar.'), dismissBtn: true });
    }
  }

  async downloadExcel(): Promise<void> {
    const rows = (this.dataSource.filteredData ?? []) as any[];
    const data: any[] = [];
    for (const r of rows) for (const l of (r.lines ?? [])) data.push({
      folio: r.folio, date: fmtDate(r.createdOn), place: r._place, warehouse: r._warehouse, requestedBy: r.requestedBy ?? '',
      neededBy: fmtDate(r.neededBy, false), code: l.code, description: l.description, quantity: l.quantity,
      source: (r.supplyPlan ?? []).filter((p: any) => p.product === l.product).map((p: any) => `${p.quantity} de ${p.label}`).join(' · '),
      status: r.status,
    });
    const cols = [['folio', 'Folio'], ['date', 'Fecha'], ['requestedBy', 'Solicita'], ['place', 'Hospital'], ['warehouse', 'Locker'],
      ['neededBy', 'Se necesita'], ['code', 'Código'], ['description', 'Descripción'],
      ['quantity', 'Piezas'], ['source', 'Se surte de'], ['status', 'Estado']].map(([k, n]) => ({ columnKey: k, columnName: n, type: 'string' as const }));
    await this.excel.simpleXLSX({ fileName: `Solicitudes de material ${new Date().toISOString().slice(0, 10)}`,
      sheets: [{ sheetName: 'Solicitudes', columns: cols as any, data, header: [], lineBreak: 0 }] });
  }
}

function r_codes(lines: any[]): number { return new Set((lines ?? []).map(l => l.product || l.code)).size; }
